import React, { useEffect, useRef } from 'react';
import { X, CheckCircle, AlertTriangle, Info, AlertOctagon } from 'lucide-react';

const InfoModal = ({
    isOpen,
    onClose,
    title,
    message,
    type = 'info',
    onConfirm,
    children,
    isLoading,
    className,
    showFooter = true,
    confirmText = 'Evet, Onayla',
    cancelText = 'İptal',
    confirmDisabled = false
}) => {
    const modalRef = useRef(null);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
            document.body.style.cursor = 'default';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const getTypeConfig = () => {
        switch (type) {
            case 'success':
                return {
                    icon: <CheckCircle className="w-6 h-6 text-green-500" />,
                    bg: 'bg-green-50 dark:bg-green-900/20',
                    border: 'border-green-100 dark:border-green-900/30'
                };
            case 'error':
                return {
                    icon: <AlertOctagon className="w-6 h-6 text-red-500" />,
                    bg: 'bg-red-50 dark:bg-red-900/20',
                    border: 'border-red-100 dark:border-red-900/30'
                };
            case 'warning':
                return {
                    icon: <AlertTriangle className="w-6 h-6 text-amber-500" />,
                    bg: 'bg-amber-50 dark:bg-amber-900/20',
                    border: 'border-amber-100 dark:border-amber-900/30'
                };
            case 'confirm':
                return {
                    icon: <AlertTriangle className="w-6 h-6 text-amber-500" />,
                    bg: 'bg-gray-50 dark:bg-gray-900/20',
                    border: 'border-gray-100 dark:border-gray-900/30'
                };
            default:
                return {
                    icon: <Info className="w-6 h-6 text-blue-500" />,
                    bg: 'bg-blue-50 dark:bg-blue-900/20',
                    border: 'border-blue-100 dark:border-blue-900/30'
                };
        }
    };

    const config = getTypeConfig();

    return (
        <div className="fixed inset-0 z-[100] flex justify-center items-start overflow-y-auto p-4 md:p-8">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={!isLoading ? onClose : undefined}
            />

            {/* Modal Content */}
            <div
                ref={modalRef}
                className={`relative w-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 transform transition-all animate-in fade-in zoom-in duration-300 my-auto ${className?.includes('max-w-') ? '' : 'max-w-lg'} ${className || ''}`}
            >
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 z-10 rounded-t-2xl">
                    <div className="flex items-center space-x-3">
                        {config.icon}
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white leading-none">{title}</h3>
                    </div>
                    {!isLoading && (
                        <button
                            onClick={onClose}
                            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    )}
                </div>

                {/* Body */}
                <div className="px-6 py-6">
                    {message && (
                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                            {message}
                        </p>
                    )}
                    {children}
                </div>

                {/* Footer */}
                {(type === 'confirm' || type === 'info' || type === 'warning') && showFooter && (
                    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 flex justify-end space-x-3 sticky bottom-0 border-t border-gray-100 dark:border-gray-700 rounded-b-2xl">
                        {(type === 'confirm' || type === 'warning') && (
                            <button
                                onClick={onClose}
                                disabled={isLoading}
                                className="px-5 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors duration-200"
                            >
                                {cancelText}
                            </button>
                        )}
                        {(type === 'confirm' || type === 'warning') ? (
                            <button
                                onClick={onConfirm}
                                disabled={isLoading || confirmDisabled}
                                className={`px-5 py-2 text-sm font-semibold text-white ${type === 'warning' ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-500/20' : 'bg-red-600 hover:bg-red-700 shadow-red-500/20'} disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors duration-200 flex items-center shadow-lg`}
                            >
                                {isLoading ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                                        İşlem Yapılıyor...
                                    </>
                                ) : (
                                    confirmText
                                )}
                            </button>
                        ) : (
                            <button
                                onClick={onClose}
                                className="px-6 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors duration-200"
                            >
                                Tamam
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default InfoModal;
